import * as React from "react";
import { Button, Card, Divider } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { CardContent } from "../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { homeLink, request } from "../../services/cpln";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { GvcInput } from "../../schema/types/gvc/input";
import { v4 as uuidv4 } from "uuid";
import { inputToQuery, QueryMatchOptions } from "../../schema/types/query/input";
import { getLocationTableItem, LocationTableItem } from "../../mst/kinds/location.helpers";
import { Table } from "../../newcomponents/table/table";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { formSetValue, useCpFormValue } from "./testNewForm/utils";
import { store } from "../../store";
import { CpFormItem } from "./testNewForm/CpFormItem";
import { CpLabel } from "./testNewForm/CpLabel";
import { CpSwitch } from "./testNewForm/CpSwitch";
import { CpSelect } from "./testNewForm/CpSelect";
import { CpErrorWarning } from "./testNewForm/CpErrorWarning";
import { CpArrayObject } from "./testNewForm/CpArrayObject";
import { CpArrayItemQueryTerm } from "../policy/targetQuery/CpArrayItemQueryTerm";
import { CpErrorWarningArrayIcon } from "./testNewForm/CpErrorWarningArrayIcon";

interface Props {}
export const LocationQuery: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const query = useCpFormValue(`locationQuery`);
  const queryResult = useCpFormValue(`meta.queryResult.location.data`);
  const queryResultIsCollapsed = useCpFormValue(`meta.queryResult.location.isCollapsed`);

  const [isEvaluatingQuery, setIsEvaluatingQuery] = React.useState(false);
  async function evaluateQuery() {
    try {
      setIsEvaluatingQuery(true);
      const form = store.getState().form.instances[id];
      const values: GvcInput = form.values as any;

      const queryApi = inputToQuery(values.locationQuery, "location");
      const { data } = await request({
        method: "post",
        url: `${homeLink("location")}/-query`,
        body: queryApi,
      });
      formSetValue(id, `meta.queryResult.location.data`, data);
      setIsEvaluatingQuery(false);
    } catch (e) {
      formSetValue(id, `meta.queryResult.location.data`, null);
      setIsEvaluatingQuery(false);
    }
  }

  const locationTableItems = (queryResult?.items || []).map((i) => getLocationTableItem({ item: i }));

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <div>Location Query</div>
              <div style={{ transform: "translateY(0px)" }}>
                <CpErrorWarningArrayIcon names={["locationQuery"]} />
              </div>
            </div>
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`locationQuery.isEnabled`}>Configure Location Query</CpLabel>
                <CpSwitch name={`locationQuery.isEnabled`} />
              </div>
            </CpFormItem>
            <CardCollapsibleContent collapsed={!query.isEnabled}>
              <CpFormItem>
                <CpLabel name={`locationQuery.match`}>Match</CpLabel>
                <CpSelect name={`locationQuery.match`} options={QueryMatchOptions} style={{ width: 250 }} />
                <CpErrorWarning name={`locationQuery.match`} />
              </CpFormItem>
              <CpArrayObject
                name={`locationQuery.terms`}
                labels={[
                  { label: "Type", span: 1 },
                  { label: "Key", span: 2 },
                  { label: "Op", span: 1 },
                  { label: "Value", span: 2 },
                ]}
                label={`Terms`}
                factoryFn={() => ({ id: uuidv4(), key: "", termType: "tag", op: "=", value: "" })}
              >
                <CpArrayItemQueryTerm kind={`location`} />
              </CpArrayObject>
              <Divider />
              <CardTitle style={queryResultIsCollapsed ? { marginBottom: 0 } : undefined}>
                <div className={`flex items-baseline ${queryResultIsCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      formSetValue(id, `meta.queryResult.location.isCollapsed`, !queryResultIsCollapsed);
                    }}
                  >
                    <CaretRightOutlined rotate={queryResultIsCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Query Evaluation</span>
                  </div>
                  <div className="flex-grow" />
                </div>
              </CardTitle>
              <CardCollapsibleContent collapsed={queryResultIsCollapsed}>
                <Button
                  htmlType={`button`}
                  block
                  type={"default"}
                  onClick={() => evaluateQuery()}
                  disabled={isEvaluatingQuery}
                  loading={isEvaluatingQuery}
                >
                  Evaluate Query
                </Button>
                {queryResult ? (
                  <Table<LocationTableItem>
                    tableId="gvc-location-query-result"
                    data={locationTableItems}
                    hideSettings
                    columns={[
                      NameDescriptionColumn({ enableSort: false }, { shouldForceTargetBlank: () => true }), //
                      TagsColumn(),
                      CreatedColumn(),
                      LastModifiedColumn(),
                    ]}
                  />
                ) : null}
              </CardCollapsibleContent>
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
